import type {
  ProcessingOptions,
  ProcessingResult,
} from "~/components/assistant/lib/messageTypeRegistry";
import type { Message, MessagePart, ToolCall } from "~/components/assistant/types";

// Tools to ignore in the UI
export const TOOL_IGNORE_LIST = ["catalog_api", "update_long_term_memory"];

/**
 * Mark all running tool calls as cancelled and add a cancelled part if not present.
 * Returns a new parts array with the modifications applied.
 */
export function markPartsAsCancelled(parts: MessagePart[]): MessagePart[] {
  // Mark all running tool calls as cancelled
  const updatedParts = parts.map((part) => {
    if (part.type === "toolCall" && part.toolCall?.running) {
      return {
        ...part,
        toolCall: { ...part.toolCall, running: false, cancelled: true },
      };
    }
    return part;
  });

  // Add cancelled part if not already present
  const hasCancelledPart = updatedParts.some((p) => p.type === "cancelled");
  if (!hasCancelledPart) {
    updatedParts.push({ type: "cancelled", order: updatedParts.length });
  }

  return updatedParts;
}

/**
 * Convert value to string for processing
 */
function stringify(value: unknown): string {
  return typeof value === "string" ? value : value ? JSON.stringify(value) : "";
}

/**
 * Find the agent message that should be updated for the current event.
 * Uses trace_id to match history messages, or finds the streaming optimistic message.
 */
function findTargetAgentMessage(messages: Message[], event: any): Message | undefined {
  const eventTraceId = event.trace_id;

  // Search backwards (most recent messages first)
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.kind !== "model_chat") {
      continue;
    }

    // Match by trace ID (for history messages that already have trace IDs)
    if (eventTraceId && msg.traceId === eventTraceId) {
      return msg;
    }

    // Match optimistic message (streaming, no trace ID yet)
    // This handles the case where API events start arriving with a trace_id
    // for a message we created optimistically
    if (msg.streaming && !msg.traceId) {
      return msg;
    }
  }

  return undefined;
}

/**
 * Process tool_call and tool_return events from the stream.
 * Creates or updates tool call items in agent messages.
 */
export function processToolMessage(
  messages: Message[],
  event: any,
  options: ProcessingOptions
): ProcessingResult {
  // Ignore blacklisted tools (unless debug mode is enabled)
  if (!options.debugMode && TOOL_IGNORE_LIST.some((prefix) => event.tool?.startsWith(prefix))) {
    return { messages };
  }

  let updatedMessages = [...messages];

  // Find or create the agent message to hold tool output
  let targetMessage = findTargetAgentMessage(messages, event);

  if (!targetMessage) {
    // No existing message found - create new agent message
    const newMessageId = options.nextMessageId();
    const modelMsg: Message = {
      id: newMessageId,
      content: "",
      kind: "model_chat",
      timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
      parts: [],
      streaming: false,
      traceId: event.trace_id || options.traceId,
    };
    updatedMessages.push(modelMsg);
    targetMessage = modelMsg;
  }

  const callId = event.tool_call_id;
  if (!callId) {
    return { messages: updatedMessages };
  }

  const stringArgs = stringify(event.args);
  const stringContent = stringify(event.content);

  if (!stringArgs && !stringContent && !event.tool) {
    return { messages: updatedMessages };
  }

  const isToolReturn = event.kind === "tool_return";

  // Update the agent message with tool call information
  updatedMessages = updatedMessages.map((m) => {
    if (m.id !== targetMessage!.id) {
      return m;
    }

    const parts: MessagePart[] = [...(m.parts || [])];

    const existingIndex = parts.findIndex(
      (p) => p.type === "toolCall" && p.toolCall?.tool_call_id === callId
    );

    // Get or create tool call
    const toolCall: ToolCall =
      existingIndex !== -1
        ? { ...parts[existingIndex].toolCall! }
        : {
            tool_call_id: callId,
            tool: "",
            toolName: "",
            args: {},
            content: {},
            timestamp: "",
            running: true,
          };

    // Update tool call with args
    if (stringArgs) {
      try {
        const parsedArgs = JSON.parse(stringArgs);
        toolCall.args = { ...toolCall.args, ...parsedArgs };
      } catch (error) {
        toolCall.args = { ...toolCall.args, raw: stringArgs };
      }
    }

    // Update tool call with content
    if (stringContent) {
      try {
        const parsedContent = JSON.parse(stringContent);
        toolCall.content = { ...toolCall.content, ...parsedContent };
      } catch (error) {
        toolCall.content = { ...toolCall.content, raw: stringContent };
      }
    }

    // Mark as completed when we receive tool_return
    if (isToolReturn) {
      toolCall.running = false;
    }

    toolCall.tool = event.tool || toolCall.tool;
    toolCall.toolName = event.tool_name || toolCall.toolName || "(unknown)";
    toolCall.timestamp = event.timestamp ?? toolCall.timestamp;

    /**
     * FIXME: Temporary hack to ensure only one tool call per tool name shows progress at a time.
     * The API should return a proper error when a tool fails to complete. Currently, orphaned
     * tool calls never resolve, leaving us with no indication of whether they succeeded or failed.
     */
    if (existingIndex === -1 && toolCall.toolName) {
      parts.forEach((part) => {
        if (
          part.type === "toolCall" &&
          part.toolCall &&
          part.toolCall.toolName === toolCall.toolName &&
          part.toolCall.tool_call_id !== callId
        ) {
          part.toolCall.running = false;
        }
      });
    }

    // Add or update tool call in parts
    if (existingIndex === -1) {
      parts.push({ type: "toolCall", toolCall, order: parts.length });
    } else {
      parts[existingIndex] = { ...parts[existingIndex], toolCall };
    }

    // Update trace ID if event has one and message doesn't (optimistic message case)
    const traceId = event.trace_id || m.traceId;

    return { ...m, parts, traceId };
  });

  return { messages: updatedMessages };
}

/**
 * Process user_chat events from the stream.
 * Updates existing optimistic user message if found, otherwise creates new one.
 */
export function processUserMessage(
  messages: Message[],
  event: any,
  options: ProcessingOptions
): ProcessingResult | null {
  const incomingContent = event.content || "";
  const incomingTraceId = event.trace_id;

  // Find optimistic user message (search backwards, since agent might be after it)
  let optimisticUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.kind === "user_chat" && !msg.traceId && msg.content === incomingContent) {
      optimisticUserIndex = i;
      break;
    }
    // Stop searching if we hit a user message with a trace ID (previous conversation)
    if (msg.kind === "user_chat" && msg.traceId) {
      break;
    }
  }

  if (optimisticUserIndex !== -1) {
    // Update existing optimistic message with trace ID
    const updatedMessages = messages.map((m, idx) =>
      idx === optimisticUserIndex ? { ...m, traceId: incomingTraceId } : m
    );
    return { messages: updatedMessages };
  }

  // No optimistic message - create new user message (history loading case)
  const incomingId = event.id || options.nextMessageId();
  const userMsg: Message = {
    id: incomingId,
    content: incomingContent,
    kind: "user_chat",
    timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
    traceId: incomingTraceId,
  };

  const updatedMessages = [...messages, userMsg];
  return { messages: updatedMessages };
}

/**
 * Process model_chat events from the stream.
 * Handles content, figures, dataframes, values, and feedback metadata.
 */
export function processModelMessage(
  messages: Message[],
  event: any,
  options: ProcessingOptions
): ProcessingResult {
  const hasContent = Boolean(event.content);
  const hasFigures = Array.isArray(event.figures) && event.figures.length > 0;
  const hasDataframes = Array.isArray(event.dataframes) && event.dataframes.length > 0;
  const hasValues = Array.isArray(event.values) && event.values.length > 0;

  if (!hasContent && !hasFigures && !hasDataframes && !hasValues) {
    return { messages };
  }

  let updatedMessages = [...messages];

  // Find or create the agent message
  let targetMessage = findTargetAgentMessage(messages, event);

  if (!targetMessage) {
    // No existing message found - create new agent message
    // Use message_id from backend if available (for chat history), otherwise generate client-side ID
    const newMessageId = event.message_id || options.nextMessageId();
    const modelMsg: Message = {
      id: newMessageId,
      content: "",
      kind: "model_chat",
      timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
      parts: [],
      streaming: false,
      traceId: event.trace_id || options.traceId,
      binaryFeedback: event.binary_feedback || null,
    };
    updatedMessages.push(modelMsg);
    targetMessage = modelMsg;
  }

  // Update the message with new parts
  updatedMessages = updatedMessages.map((m) => {
    if (m.id !== targetMessage!.id) {
      return m;
    }

    const parts: MessagePart[] = [...(m.parts || [])];
    let nextOrder = parts.length;

    if (hasContent) {
      parts.push({ type: "content", content: event.content, order: nextOrder++ });
    }

    if (hasFigures) {
      const figArray = event.figures as any[];
      for (const fig of figArray) {
        parts.push({ type: "figure", figure: fig, order: nextOrder++ });
      }
    }

    if (hasDataframes) {
      const dfArray = event.dataframes as any[];
      for (const df of dfArray) {
        parts.push({ type: "dataframe", dataframe: df, order: nextOrder++ });
      }
    }

    if (hasValues) {
      const valArray = event.values as any[];
      for (const val of valArray) {
        parts.push({ type: "value", value: val, order: nextOrder++ });
      }
    }

    // Update trace ID if event has one and message doesn't (optimistic message case)
    const traceId = event.trace_id || m.traceId;

    // Update message_id and binary_feedback if event has them (for chat history messages)
    // Use the backend message_id if this is a history message, keep client ID otherwise
    const messageId = event.message_id || m.id;
    const binaryFeedback =
      event.binary_feedback !== undefined ? event.binary_feedback : m.binaryFeedback;

    return {
      ...m,
      id: messageId,
      parts,
      traceId,
      binaryFeedback,
    };
  });

  return { messages: updatedMessages };
}

/**
 * Process error events from the stream.
 * Removes optimistic messages and adds error message.
 */
export function processErrorMessage(
  messages: Message[],
  event: any,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  options: ProcessingOptions
): ProcessingResult {
  // Find and remove the optimistic agent message if it exists
  const targetMessage = findTargetAgentMessage(messages, event);
  const filtered = targetMessage ? messages.filter((m) => m.id !== targetMessage.id) : messages;

  // Add error message with trace ID
  const errorMessage: Message = {
    id: `error-${Date.now()}`,
    content: "",
    kind: "model_chat",
    timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
    error: event.message || event.error || "An error occurred",
    traceId: event.trace_id,
  };

  return { messages: [...filtered, errorMessage] };
}

/**
 * Process warning events from the stream.
 * Removes optimistic messages and adds warning message.
 */
export function processWarningMessage(
  messages: Message[],
  event: any,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  options: ProcessingOptions
): ProcessingResult {
  // Find and remove the optimistic agent message if it exists
  const targetMessage = findTargetAgentMessage(messages, event);
  const filtered = targetMessage ? messages.filter((m) => m.id !== targetMessage.id) : messages;

  // Add warning message with trace ID
  const warningMessage: Message = {
    id: `warning-${Date.now()}`,
    content: "",
    kind: "model_chat",
    timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
    warning: event.message || "A warning occurred",
    traceId: event.trace_id,
  };

  return { messages: [...filtered, warningMessage] };
}

/**
 * Process cancelled events from the stream.
 * Marks running tool calls as cancelled and adds cancellation message.
 * If no existing agent message exists (e.g., cancelled before any response),
 * creates a new cancelled message.
 */
export function processCancelledMessage(
  messages: Message[],
  event: any,
  options: ProcessingOptions
): ProcessingResult {
  // Find the streaming agent message
  const targetMessage = findTargetAgentMessage(messages, event);

  if (!targetMessage) {
    // No existing agent message - create a new cancelled message
    // This happens when loading history where stream was cancelled before any response
    const cancelledMessage: Message = {
      id: options.nextMessageId(),
      content: "",
      kind: "model_chat",
      timestamp: event.timestamp ? new Date(event.timestamp) : new Date(),
      parts: [{ type: "cancelled", order: 0 }],
      streaming: false,
      cancelled: true,
      traceId: event.trace_id,
    };
    return { messages: [...messages, cancelledMessage] };
  }

  const updatedMessages = messages.map((m) => {
    if (m.id !== targetMessage?.id) {
      return m;
    }

    return {
      ...m,
      parts: markPartsAsCancelled(m.parts || []),
      streaming: false,
      cancelled: true,
      traceId: event.trace_id || m.traceId,
    };
  });

  return { messages: updatedMessages };
}

/**
 * Mark a message as no longer streaming.
 */
export function markMessageComplete(messages: Message[], messageId: string): Message[] {
  return messages.map((m) => (m.id === messageId ? { ...m, streaming: false } : m));
}

/**
 * Main stream event processor.
 * Delegates to specific processors based on event kind.
 */
export function processStreamEvent(
  messages: Message[],
  event: any,
  options: ProcessingOptions
): ProcessingResult | null {
  if (!event) {
    return null;
  }

  // Ignore heartbeat messages - they're only for keeping the connection alive
  if (event.kind === "heartbeat") {
    return null;
  }

  // Handle conversation_name events - auto-generated conversation titles
  if (event.kind === "conversation_name") {
    return {
      messages,
      conversationName: {
        conversationId: event.conversation_id,
        displayName: event.display_name,
      },
    };
  }

  // Handle error events (both old format with event.error and new format with kind="error")
  if (event.error || event.kind === "error") {
    return processErrorMessage(messages, event, options);
  }

  // Handle warning events
  if (event.kind === "warning") {
    return processWarningMessage(messages, event, options);
  }

  // Handle cancelled events (backend ACK for cancellation)
  if (event.kind === "cancelled") {
    return processCancelledMessage(messages, event, options);
  }

  // Handle user chat events
  if (event.kind === "user_chat") {
    return processUserMessage(messages, event, options);
  }

  // Handle model chat events
  if (event.kind === "model_chat") {
    return processModelMessage(messages, event, options);
  }

  // Handle tool events
  if (event.kind === "tool_call" || event.kind === "tool_return") {
    return processToolMessage(messages, event, options);
  }

  return null;
}
